/**
 * shared/formFieldValidation.ts
 *
 * Shape validation for fields a HUMAN types into the Control Center or the
 * admin app — the mirror of `backend/functions/src/utils/formFieldValidation.ts`,
 * which does the same job for a visitor filling in a client's website form.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * The 2026-08-23 form-validation work covered WEBSITE forms only. The Control
 * Center — where a business OWNER types the numbers and addresses we then dial,
 * text, invoice and publish — had none of it. That is the higher-consequence
 * surface of the two: a visitor's typo costs one lead, but an owner's typo in a
 * transfer number, a roster contact or an invoice recipient is wrong for every
 * call and every document until somebody notices.
 *
 * ── The rule this encodes ───────────────────────────────────────────────────
 * PLAUSIBLE IS NOT DIALABLE. A value that merely passes a shape check and is
 * then stored AS TYPED is the defect webFormCapture had: "(406) 555-1234" landed
 * in a field that is an SMS destination. So a surface using this must do BOTH —
 * warn on the shape here, and normalise before the value is stored.
 *
 * ── Why a duplicate, and how it is kept honest ──────────────────────────────
 * The React workspace cannot import from `backend/functions/` (different
 * tsconfig, deps and bundle target) — the same constraint that produced
 * `shared/connectedAccounts.ts`. What is different here is that the duplication
 * is GATED, not promised: `backend/functions/src/__tests__/shared-formFieldValidation.test.ts`
 * imports BOTH copies and runs one shared corpus through them, asserting
 * identical verdicts on every case. The backend file stays canonical; this file
 * cannot silently disagree with it. [[single-source-of-truth]]
 */

/** What a field is asking for, inferred from its name and input type. */
export type FieldKind = 'email' | 'phone' | 'url' | 'other'

/** Normalize a field key: lowercase, alphanumeric. */
function normKey(key: string): string {
  return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

const EMAIL_HINTS = ['email', 'emailaddress', 'youremail', 'mail', 'contactemail']
const PHONE_HINTS = [
  'phone', 'tel', 'telephone', 'phonenumber', 'mobile', 'cell', 'yourphone',
  'cellphone', 'mobilephone', 'mobilephonenumber', 'cellphonenumber',
  'phonemobile', 'contactphone', 'daytimephone', 'primaryphone',
  'alternatephonenumber', 'alternatephone', 'secondaryphone', 'fax',
]
const URL_HINTS = ['website', 'url', 'weburl', 'websiteurl', 'homepage', 'siteurl']

/** PURE — what kind of value does this field want? */
export function classifyFieldKind(name: string, inputType?: string): FieldKind {
  const t = String(inputType || '').toLowerCase()
  if (t === 'email') return 'email'
  if (t === 'tel') return 'phone'
  if (t === 'url') return 'url'
  const k = normKey(name)
  if (EMAIL_HINTS.includes(k)) return 'email'
  if (PHONE_HINTS.includes(k)) return 'phone'
  if (URL_HINTS.includes(k)) return 'url'
  if (/email|^mail/.test(k)) return 'email'
  if (/phone|mobile|^tel/.test(k)) return 'phone'
  // A field whose name ENDS in "link" wants a URL. Added 2026-09-06 for the
  // business-profile variables GOOGLE_REVIEW_LINK and FACEBOOK_PAGE_LINK, whose
  // values are published into client content — and it is equally true of a
  // website form field called "link".
  if (/^website|^weburl|link$/.test(k)) return 'url'
  return 'other'
}

/**
 * PURE — does this look like an email address a human could receive mail at?
 * Practical, not RFC-complete: a regex strict enough to be "correct" rejects
 * real people.
 */
export function isPlausibleEmail(value: string): boolean {
  const v = String(value || '').trim()
  if (!v || v.length > 254) return false
  if (/\s/.test(v)) return false
  return /^[^@]+@[^@.]+(\.[^@.]+)+$/.test(v)
}

/**
 * PURE — reduce a phone to digits. A TRAILING extension is cut; it is not part
 * of the dialable number. The obvious `\b(?:x|ext)\b` does not match the
 * commonest form "555-1234 x22", so this anchors on the digits that follow.
 */
export function phoneDigits(value: string): string {
  const v = String(value || '').replace(/\s*(?:\bext(?:ension)?\.?|\bx\.?)\s*\d+\s*$/i, '')
  return v.replace(/\D/g, '')
}

/**
 * PURE — could this be dialled?
 *
 * A NINE-digit US number is a typo every time, so only an EXPLICITLY
 * international number (leading + or 00) may be shorter than ten digits.
 */
export function isPlausiblePhone(value: string): boolean {
  const d = phoneDigits(value)
  if (d.length === 10) return true
  if (d.length === 11 && d.startsWith('1')) return true
  const international = /^\s*(?:\+|00)/.test(String(value || ''))
  if (international) return d.length >= 8 && d.length <= 15
  return d.length >= 11 && d.length <= 15
}

/** PURE — does this look like a web address? Scheme optional, as people type it. */
export function isPlausibleUrl(value: string): boolean {
  const v = String(value || '').trim()
  if (!v || /\s/.test(v)) return false
  const withScheme = /^https?:\/\//i.test(v) ? v : `https://${v}`
  try {
    const u = new URL(withScheme)
    return u.hostname.includes('.') && !u.hostname.endsWith('.')
  } catch { return false }
}

/** One field the person should be asked to correct before saving. */
export interface FieldProblem {
  field: string
  kind: FieldKind
  message: string
}

/**
 * PURE — check every value against what its field is asking for.
 *
 * Empty is NOT a problem here: whether a field is required is the form's
 * business, not this function's. Returns EVERY problem, never just the first —
 * fixing one error only to be shown the next is the thing people abandon.
 */
export function findFieldProblems(
  fields: Record<string, string>,
  types: Record<string, string> = {},
): FieldProblem[] {
  const problems: FieldProblem[] = []
  for (const [name, raw] of Object.entries(fields || {})) {
    const value = typeof raw === 'string' ? raw.trim() : ''
    if (!value) continue
    const kind = classifyFieldKind(name, types[name])
    if (kind === 'email' && !isPlausibleEmail(value)) {
      problems.push({ field: name, kind, message: 'That email address looks incomplete — please check it.' })
    } else if (kind === 'phone' && !isPlausiblePhone(value)) {
      problems.push({ field: name, kind, message: 'That phone number looks incomplete — please check it.' })
    } else if (kind === 'url' && !isPlausibleUrl(value)) {
      problems.push({ field: name, kind, message: 'That web address looks incomplete — please check it.' })
    }
  }
  return problems
}

// ── The UI layer ────────────────────────────────────────────────────────────

/**
 * Map of field name → message, which is the shape a React form wants.
 */
export function fieldProblemMap(
  fields: Record<string, string>,
  types: Record<string, string> = {},
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const p of findFieldProblems(fields, types)) out[p.field] = p.message
  return out
}

/**
 * The props that mark one input as invalid, so every surface says it the same
 * way and none of them forgets the accessibility half.
 *
 * `aria-invalid` and a described-by pointing at the message are what make the
 * red outline mean anything to somebody not looking at it.
 */
export function invalidFieldProps(
  field: string,
  problem: string | undefined,
): { 'aria-invalid'?: 'true'; 'aria-describedby'?: string } {
  if (!problem) return {}
  return { 'aria-invalid': 'true', 'aria-describedby': `${field}-error` }
}

/** Tailwind classes appended to an input that is currently wrong. */
export const INVALID_INPUT_CLASS = 'border-red-500 dark:border-red-500 focus:border-red-500'
